import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
import PetSearch from '../PetSearch';
import { NewBookingAlert } from '@spike/new-booking-model';
import { useApiClientWrapper } from 'hooks';
import { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { fetchAlertsThunk } from '@spike/new-bookings-action';
import DateSelector from '../DateSelector';
import { AddOnDate, Discount as DiscountModel, itemTypes, versionPetAndServices } from 'model/NewMultipleBooking';
import ProductSelector from '../ProductSelector';
import { Product } from '@spike/product-model';
import AddOnSelector from '../AddOnSelector';
import AddOnService from '@spike/addon-service-model';
import { TextFieldNoLabel } from 'components/UI';
import QuantityAndPrice from '../QuantityAndPrice';
import clsx from 'clsx';
import ExtraStaffSelector from '../StaffSelector/ExtraStaffSelector';
import Discount from '../Discount';
import FooterComponent from '../FooterComponent';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import useNonInitialEffect from '@versiondos/hooks';

interface Props {
    parentId: number;
    petId?: number;
    versionPetAndServices?: versionPetAndServices;
    date?: Moment;
    type?: itemTypes;
    product?: Product;
    addOn?: AddOnService;
    custom?: string;
    discount?: DiscountModel;
    quantity?: number;
    amount?: number;
    staffId?: number;
    itemsUsed?: Array<number>;
    loading?: boolean;
    petWithoutSelect?: boolean;
    dates?: Array<AddOnDate>;
    changedDate?: boolean;
    onChangeDate?: (value: Moment) => void;
    onBook: (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: DiscountModel,
        quantity?: number,
        amount?: number
    ) => void;
    onRemove?: (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string
    ) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        sectionContainer: {
            paddingTop: 18,
            [theme.breakpoints.up('md')]: {
                paddingTop: 20
            }
        },
        divide: {
            borderBottom: '1px solid #D4D4D4'
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginTop: 8,
            marginBottom: 10,
            [theme.breakpoints.up('md')]: {
                marginBottom: 12
            }
        },
        total: {
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        quantityAndPriceContainer: {
            display: 'flex',
            width: '100%'
        },
        quantityAndPriceContainerVertical: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        marginTop: {
            marginTop: 10
        },
        quantity: {
            width: '44%',
            marginRight: 20
        },
        discount: {
            width: '52%'
        },
        quantityVertical: {
            width: '100%',
            marginBottom: 30
        },
        discountVertical: {
            width: '100%'
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4',
            marginTop: 11,
            marginBottom: 5
        },
        content: {
            margin: '0px 26px 0px 26px',
            [theme.breakpoints.up('md')]: {
                margin: '0px 30px 0px 30px'
            }
        }
    })
);

export const AddOrEditComplementaryItem: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [date, setDate] = useState<Moment | undefined>(props.date);
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(props.product);
    const [selectedAddOn, setSelectedAddOn] = useState<AddOnService | undefined>(props.addOn);
    const [custom, setCustom] = useState<string | undefined>(props.custom);
    const [quantity, setQuantity] = useState<number>(props.quantity || 1);
    const [amount, setAmount] = useState<number>(props.amount || 0);
    const [staffId, setStaffId] = useState<number | undefined>(props.staffId);
    const [changedDate, setChangedDate] = useState<boolean>(!!props.changedDate);
    const [discount, setDiscount] = useState<DiscountModel>(
        props.discount || {
            type: 'amount',
            value: 0
        }
    );
    const [disabledButton, setDisabledButton] = useState<boolean>(false);
    const [defaultAmount, setDefaultAmount] = useState<string | undefined>(undefined);
    const [defaultDiscount, setDefaultDiscount] = useState<string | undefined>(undefined);

    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);

    useEffect(() => {
        if (date) {
            dispatch(
                fetchAlertsThunk(
                    apiClientWrapper,
                    props.parentId || 0,
                    props.petId || 0,
                    date.clone().tz('utc'),
                    false,
                    undefined,
                    staffId,
                    undefined
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.date, date, selectedProduct, selectedAddOn, custom, staffId]);

    useNonInitialEffect(() => {
        if (props.date?.format('L') !== date?.format('L') && props.type === itemTypes.addOn) {
            props.onChangeDate && props.onChangeDate(date!);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    useEffect(() => {
        if (isNaN(Number(discount.value))) {
            setDisabledButton(true);
        } else {
            setDisabledButton(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discount]);

    useNonInitialEffect(() => {
        if (props.type !== itemTypes.custom) {
            setQuantity(1);
            setDiscount({
                type: 'amount',
                value: 0
            });
            setDefaultDiscount('0.00');
            setStaffId(undefined);
        }

        if (props.type === itemTypes.addOn) {
            setAmount(selectedAddOn?.price ? Number(selectedAddOn?.price) : 0);
            setDefaultAmount(selectedAddOn?.price ? selectedAddOn?.price?.toString() : '0.00');
        }

        if (props.type === itemTypes.product) {
            setAmount(
                selectedProduct?.businessProduct?.pricing?.price
                    ? Number(selectedProduct?.businessProduct?.pricing?.price)
                    : 0
            );
            setDefaultAmount(
                selectedProduct?.businessProduct?.pricing?.price
                    ? selectedProduct?.businessProduct?.pricing?.price?.toString()
                    : '0.00'
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddOn, selectedProduct, custom]);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultAmount || defaultDiscount) {
                setDefaultAmount(undefined);
                setDefaultDiscount(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultAmount, defaultDiscount]);

    useEffect(() => {
        if (!selectedAddOn && !selectedProduct && !custom) {
            setDisabledButton(true);
        } else {
            setDisabledButton(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddOn, selectedProduct, custom]);

    useNonInitialEffect(() => {
        setDiscount({
            type: 'amount',
            value: 0
        });
        setDefaultDiscount('0.00');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quantity, amount]);

    const handlerChangeDate = (value: Moment) => {
        setDate(value);
        if (date?.format('L') !== value.format('L')) {
            setChangedDate(true);
        }
    };

    const handlerBook = () => {
        props.onBook(
            date,
            props.petId,
            props.type === itemTypes.product ? selectedProduct : undefined,
            props.type === itemTypes.addOn ? selectedAddOn : undefined,
            props.type === itemTypes.custom ? custom : undefined,
            staffId,
            discount,
            quantity,
            amount
        );
    };

    const handleRemove = () => {
        props.onRemove &&
            props.onRemove(
                date,
                props.petId,
                props.type === itemTypes.product ? selectedProduct : undefined,
                props.type === itemTypes.addOn ? selectedAddOn : undefined,
                props.type === itemTypes.custom ? custom : undefined
            );
    };

    const getTotal = () => {
        const total: number = quantity * amount;
        if (discount.type === 'percentage') {
            const totalAmount = discount.value ? ((100 - discount.value) * total) / 100 : total;
            return totalAmount.toFixed(2);
        } else {
            const totalAmount = discount.value ? total - discount.value : total;
            return totalAmount.toFixed(2);
        }
    };

    const petsView = (
        <Box className={classes.sectionContainer}>
            <PetSearch
                parentID={props.parentId}
                petId={props.petId}
                multiplePets={true}
                readOnly={true}
                hideSelect={props.petWithoutSelect || false}
                handlerPet={() => {}}
            />
        </Box>
    );

    const dateView = (
        <Box className={classes.sectionContainer}>
            <DateSelector
                date={date!}
                isRecurrent={false}
                withoutBorder
                showAlertBusinessHour={
                    alerts && alerts?.filter(alert => alert.alertType === 'date_time')?.length > 0 ? true : false
                }
                multiplePet={true}
                onChangeFrequencyDuration={() => {}}
                onChangeRecurrent={() => {}}
                onChangeDate={handlerChangeDate}
                alerts={alerts?.filter(alert => alert.alertType === 'unified')}
            />
        </Box>
    );

    const productsView = (
        <Box className={classes.sectionContainer}>
            <ProductSelector
                productsUsed={props.itemsUsed}
                selectedProduct={selectedProduct}
                onSelectProduct={setSelectedProduct}
            />
        </Box>
    );

    const addOnView = (
        <Box className={classes.sectionContainer}>
            <AddOnSelector
                changedDate={changedDate}
                addOnsUsed={props.itemsUsed}
                selectedAddOn={selectedAddOn}
                date={date}
                dates={props.dates}
                onSelectAddOn={setSelectedAddOn}
            />
        </Box>
    );

    const customView = (
        <Box className={classes.sectionContainer}>
            <Typography className={classes.title}>Description</Typography>
            <TextFieldNoLabel
                placeholder="Enter Description"
                value={custom || ''}
                onChange={setCustom}
            />
        </Box>
    );

    const totalView = (
        <Box className={classes.sectionContainer}>
            <Typography className={classes.title}>Total</Typography>
            <Typography className={classes.total}>{`$${getTotal()}`}</Typography>
        </Box>
    );

    const quantityPriceAndDiscountView = (
        <Box className={clsx(classes.sectionContainer, classes.marginTop)}>
            <Box className={isSmall ? classes.quantityAndPriceContainerVertical : classes.quantityAndPriceContainer}>
                <Box className={isSmall ? classes.quantityVertical : classes.quantity}>
                    <QuantityAndPrice
                        quantity={quantity}
                        amount={amount}
                        default={defaultAmount}
                        onChangeQuantity={setQuantity}
                        onChangeAmount={(value: number | undefined) => setAmount(value || 0)}
                    />
                </Box>
                <Box className={isSmall ? classes.discountVertical : classes.discount}>
                    <Discount
                        discount={discount}
                        max={
                            discount.type === 'percentage' ? 100 : discount.type === 'amount' ? amount * quantity : 0.5
                        }
                        default={defaultDiscount}
                        onChangeDiscount={setDiscount}
                    />
                </Box>
            </Box>
        </Box>
    );

    const staffView = (
        <Box className={classes.sectionContainer}>
            <ExtraStaffSelector
                selectedStaff={staffId}
                onSelectStaff={setStaffId}
            />
        </Box>
    );

    return (
        <Box className={classes.container}>
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <Box className={classes.content}>
                    {petsView}
                    {dateView}
                    {props.type === itemTypes.product && productsView}
                    {props.type === itemTypes.addOn && addOnView}
                    {props.type === itemTypes.custom && customView}
                    {(selectedProduct || selectedAddOn || custom) && quantityPriceAndDiscountView}
                    {(selectedProduct || selectedAddOn || custom) && totalView}
                    {(selectedProduct || selectedAddOn || custom) && staffView}
                </Box>
            </OverlayScrollbarsComponent>
            <Box className={classes.totalContainer}>
                <FooterComponent
                    loading={props.loading || false}
                    buttonText="Save"
                    withoutTotal={true}
                    disabledButton={disabledButton}
                    onBook={handlerBook}
                    onRemove={props.versionPetAndServices === versionPetAndServices.edit ? handleRemove : undefined}
                />
            </Box>
        </Box>
    );
};

export default AddOrEditComplementaryItem;
